import React, { useEffect, useMemo, useState } from "react";
import { ConfigProvider } from "./contexts/ConfigContext.jsx";
import { ThemeProvider } from "./contexts/ThemeContext.jsx";

import TopBar from "./components/layout/TopBar.jsx";
import Header from "./components/layout/Header.jsx";
import LeftSidebar from "./components/layout/LeftSidebar.jsx";
import RightSidebar from "./components/layout/RightSidebar.jsx";
import Footer from "./components/layout/Footer.jsx";
import BottomBar from "./components/layout/BottomBar.jsx";
import ScrollControls from "./components/layout/ScrollControls.jsx";
import HekaChat from "./components/HekaChat.jsx";

import { PAGE_COMPONENTS } from "./pages/pageRegistry.js";
import { useLocation, useNavigate } from "react-router-dom";

/* ========================================================= */
/* ================= ROUTE MAPPINGS ======================== */
/* ========================================================= */

const PAGE_TO_PATH = {
  Home: "/",
  "Vision & Philosophy": "/vision",
  Products: "/products",
     HekaAtlas : "/products/HekaAtlas",
     HekaCodex : "/products/HekaCodex",
     HekaOdyssey : "/products/HekaOdyssey",
  "Intelligence Corner": "/intelligence",
     Article: "/resources/articles",
     Documentation: "/resources/documentation",
     APIReference: "/resources/api-reference",
  Connect: "/connect",
  Career: "/career",

};

const PATH_TO_PAGE = Object.fromEntries(
  Object.entries(PAGE_TO_PATH).map(([page, path]) => [path, page])
);

/* ========================================================= */
/* ====================== APP LAYOUT ======================= */
/* ========================================================= */

const AppLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();

  /* ---------- PAGE STATE ---------- */
  const [currentPage, setCurrentPage] = useState("Home");

  /* ---------- SIDEBAR STATE ---------- */
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(false);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  /* ---------- SYNC URL → PAGE (on refresh/direct link) ---------- */
  useEffect(() => {
    const pageFromPath = PATH_TO_PAGE[location.pathname];
    if (pageFromPath && pageFromPath !== currentPage) {
      setCurrentPage(pageFromPath);
    }
  }, [location.pathname]); // eslint-disable-line

  /* ---------- SYNC PAGE → URL (when sidebar changes page) ---------- */
  useEffect(() => {
    const newPath = PAGE_TO_PATH[currentPage];
    if (newPath && newPath !== location.pathname) {
      navigate(newPath);
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]); // eslint-disable-line

  /* ---------- LOAD SIDEBAR STATE ---------- */
  useEffect(() => {
    if (!isDesktop) return;

    try {
      const left = localStorage.getItem("hekaos-left-sidebar");
      const right = localStorage.getItem("hekaos-right-sidebar");

      setLeftSidebarOpen(left !== null ? JSON.parse(left) : true);
      setRightSidebarOpen(right !== null ? JSON.parse(right) : true);
    } catch {
      setLeftSidebarOpen(true);
      setRightSidebarOpen(true);
    }
  }, [isDesktop]);

  /* ---------- HANDLE RESIZE ---------- */
  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);

      if (!desktop) {
        setLeftSidebarOpen(false);
        setRightSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ---------- TOGGLE LEFT SIDEBAR ---------- */
  const toggleLeftSidebar = () => {
    const newState = !leftSidebarOpen;
    setLeftSidebarOpen(newState);

    if (isDesktop) {
      localStorage.setItem("hekaos-left-sidebar", JSON.stringify(newState));
    }
  };

  /* ---------- TOGGLE RIGHT SIDEBAR ---------- */
  const toggleRightSidebar = () => {
    const newState = !rightSidebarOpen;
    setRightSidebarOpen(newState);

    if (isDesktop) {
      localStorage.setItem("hekaos-right-sidebar", JSON.stringify(newState));
    }
  };

  /* ---------- PAGE REGISTRY ---------- */
  const PageComponent = useMemo(
    () => PAGE_COMPONENTS[currentPage] || PAGE_COMPONENTS.Home,
    [currentPage]
  );

  /* ========================================================= */
  /* ========================= UI ============================ */
  /* ========================================================= */

  return (
    <div className="min-h-screen bg-primary text-primary">
      {/* TOP STRIP */}
      <TopBar />

      {/* HEADER */}
      <Header
        toggleLeftSidebar={toggleLeftSidebar}
        toggleRightSidebar={toggleRightSidebar}
      />

      {/* BODY */}
      <div className="flex">
        {/* LEFT SIDEBAR */}
        <LeftSidebar
          isOpen={leftSidebarOpen}
          toggle={toggleLeftSidebar}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />

        {/* MAIN CONTENT */}
        <main className="flex-1 min-h-[calc(100vh-200px)] px-4 py-8">
          <PageComponent setCurrentPage={setCurrentPage} />
        </main>

        {/* RIGHT SIDEBAR */}
        <RightSidebar
          isOpen={rightSidebarOpen}
          toggle={toggleRightSidebar}
        />
      </div>

      {/* FOOTER ZONE */}
      <Footer />
      <BottomBar />

      {/* FLOATING UTILITIES */}
      <HekaChat />
      <ScrollControls />
    </div>
  );
};

/* ========================================================= */
/* ======================= PROVIDERS ======================= */
/* ========================================================= */

const App = () => (
  <ThemeProvider>
    <ConfigProvider>
      <AppLayout />
    </ConfigProvider>
  </ThemeProvider>
);

export default App;
